'use client'

import { FC, useEffect, useState } from 'react'
import { IconLoader2 } from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'

import Ripple from '@/components/ui/ripple'
import { LosiAnimated } from '../icons/losi-animated-metal'

const BOOT_COPY = 'Loading your workspace…'

const initialMessages = [
  BOOT_COPY,
  'Getting things ready…',
  'Almost there…'
]

const subsequentMessages = [
  'Still loading your workspace…',
  'Finishing setup…',
  'One more moment…'
]
const INITIAL_TIMEOUT = 2000
const MESSAGE_INTERVAL = 2500
const FADE_DURATION = 500 // Duration for fade effect in milliseconds
const DEBUG_INFO_DELAY_MS = 1500

interface LoaderProps {
  withMessage?: boolean
  debugLabel?: string
}

interface LoaderDebugInfo {
  pathname: string
  rootStage: string
  sequence: string
  label: string
}

type RootMarkerWindow = Window & {
  __LOSI_LAST_ROOT_MARKER?: {
    stage?: string
    sequence?: number
  }
}

// --------------

function readLoaderDebugInfo(label: string): LoaderDebugInfo | null {
  if (typeof window === 'undefined') {
    return null
  }

  const markerWindow = window as RootMarkerWindow
  const marker = markerWindow.__LOSI_LAST_ROOT_MARKER

  return {
    pathname: window.location.pathname,
    rootStage: marker?.stage || 'unknown',
    sequence: typeof marker?.sequence === 'number' ? String(marker.sequence) : 'n/a',
    label
  }
}

// Fisher-Yates shuffle function
const shuffleArray = (array: string[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export const Loader: FC<LoaderProps> = ({ withMessage = false, debugLabel }) => {
  // Always show progress copy on first paint — do not wait for useEffect.
  const [messageIndex, setMessageIndex] = useState(0)
  const [message, setMessage] = useState(BOOT_COPY)
  const [isVisible, setIsVisible] = useState(true)
  const [debugInfo, setDebugInfo] = useState<LoaderDebugInfo | null>(null)

  useEffect(() => {
    // Shuffle subsequent messages
    const shuffledMessages = shuffleArray([...subsequentMessages])

    const changeMessage = () => {
      setIsVisible(false) // Start fade out
      setTimeout(() => {
        setMessageIndex(prevIndex => {
          const newIndex = (prevIndex + 1) % shuffledMessages.length
          setMessage(shuffledMessages[newIndex])
          return newIndex
        })
        setIsVisible(true) // Start fade in
      }, FADE_DURATION)
    }

    const timer = setTimeout(() => {
      changeMessage() // First message change after initial timeout
      const interval = setInterval(changeMessage, MESSAGE_INTERVAL)
      return () => clearInterval(interval)
    }, INITIAL_TIMEOUT)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!debugLabel || typeof window === 'undefined') {
      return
    }

    let intervalId: ReturnType<typeof setInterval> | null = null

    const timeoutId = window.setTimeout(() => {
      setDebugInfo(readLoaderDebugInfo(debugLabel))
      intervalId = window.setInterval(() => {
        setDebugInfo(readLoaderDebugInfo(debugLabel))
      }, 1000)
    }, DEBUG_INFO_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)

      if (intervalId) {
        window.clearInterval(intervalId)
      }
    }
  }, [debugLabel])

  const debugCaption =
    process.env.NODE_ENV === 'development' && debugLabel ? (
      <div className="mt-4 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-center text-[11px] text-white/60">
        <div>{debugInfo?.label || debugLabel}</div>
        {debugInfo ? (
          <>
            <div>{debugInfo.pathname}</div>
            <div>{`root:${debugInfo.rootStage} #${debugInfo.sequence}`}</div>
          </>
        ) : null}
      </div>
    ) : null

  if (!withMessage) {
    return (
      <div className="flex size-full flex-col items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <IconLoader2 className="h-12 w-12 animate-spin text-primary/80" />
        </motion.div>
        {debugCaption}
      </div>
    )
  }

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-background">
      <div className="z-10 flex flex-col items-center justify-center text-center">
        <LosiAnimated
          size={170}
          className="text-foreground opacity-70"
        />
        <AnimatePresence mode="wait">
          <motion.p
            key={message}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: isVisible ? 1 : 0, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35 }}
            className="text-muted-foreground mt-3 max-w-xs text-sm font-medium"
          >
            {message || BOOT_COPY}
          </motion.p>
        </AnimatePresence>
        {debugCaption}
      </div>
      <Ripple mainCircleSize={270} />
    </div>
  )
}
